/**
 * Bottom dock: Jobs | Log | QC.
 *
 * The Log body is implemented here because the shell already owns the bounded
 * log buffer; Jobs and QC are slots a feature workstream fills with
 * `registerDockPanel`.
 */

import * as React from "react";
import { ChevronDown, Trash2 } from "lucide-react";

import { EmptyState } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { useAppStore, useDockTab, useLogLines } from "@/store/appStore";
import { useDockPanel, type DockTabId } from "@/shell/tabRegistry";

const DOCK_TABS: { id: DockTabId; label: string }[] = [
  { id: "jobs", label: "Jobs" },
  { id: "log", label: "Log" },
  { id: "qc", label: "QC" },
  { id: "console", label: "Console" },
];

const LEVEL_CLASS: Record<string, string> = {
  // `trace` is ANTs/PyTorch chatter: kept, but visually subordinate to the
  // structured pipeline messages.
  trace: "text-ads-faint",
  info: "text-ads-text",
  warn: "text-amber-600",
  error: "text-red-600",
};

function LogView() {
  const lines = useLogLines();
  const endRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [follow, setFollow] = React.useState(true);

  React.useEffect(() => {
    if (follow) endRef.current?.scrollIntoView({ block: "end" });
  }, [lines, follow]);

  if (lines.length === 0) {
    return <EmptyState title="No log output" hint="Pipeline output appears here while a job runs." />;
  }

  return (
    <div
      ref={scrollRef}
      onScroll={(e) => {
        const el = e.currentTarget;
        // Following is off as soon as the user scrolls away from the tail.
        setFollow(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
      }}
      className="h-full overflow-auto px-3 py-1.5 font-mono text-[11px] leading-[1.45]"
      data-selectable
    >
      {lines.map((line) => (
        <div key={line.seq} className="flex gap-2 whitespace-pre-wrap">
          <span className="shrink-0 text-ads-faint">{line.ts.slice(11, 19)}</span>
          <span className={cn(LEVEL_CLASS[line.level] ?? "text-ads-text")}>{line.line}</span>
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}

function DockBody({ tab }: { tab: DockTabId }) {
  const Registered = useDockPanel(tab);
  if (Registered) return <Registered />;
  if (tab === "log") return <LogView />;
  return (
    <EmptyState
      title={`${DOCK_TABS.find((t) => t.id === tab)?.label} panel — under construction`}
      hint="This panel is owned by another workstream."
    />
  );
}

export function Dock() {
  const tab = useDockTab();
  const setTab = useAppStore((s) => s.setDockTab);
  const togglePanel = useAppStore((s) => s.togglePanel);
  const clearLog = useAppStore((s) => s.clearLog);

  return (
    <section className="flex min-h-0 flex-1 flex-col bg-ads-surface">
      <header className="flex h-7 shrink-0 items-center border-b border-ads-border pr-1 pl-2">
        {DOCK_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "h-full border-b-2 px-3 text-[12px] transition-colors duration-100",
              t.id === tab
                ? "border-ads-accent font-medium text-ads-accent"
                : "border-transparent text-ads-muted hover:text-ads-text",
            )}
          >
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        {tab === "log" ? (
          <button
            type="button"
            title="Clear log"
            onClick={clearLog}
            className="flex h-5 w-6 items-center justify-center rounded text-ads-muted hover:bg-ads-hover"
          >
            <Trash2 size={13} />
          </button>
        ) : null}
        <button
          type="button"
          title="Collapse panel"
          onClick={() => togglePanel("bottom", true)}
          className="flex h-5 w-6 items-center justify-center rounded text-ads-muted hover:bg-ads-hover"
        >
          <ChevronDown size={14} />
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-hidden">
        <DockBody tab={tab} />
      </div>
    </section>
  );
}

/** The collapsed dock: a title strip that clicks back open. */
export function DockRail() {
  const tab = useDockTab();
  const togglePanel = useAppStore((s) => s.togglePanel);
  return (
    <button
      type="button"
      onClick={() => togglePanel("bottom", false)}
      className="flex h-6 shrink-0 items-center gap-2 border-t border-ads-border bg-ads-surface px-3 text-[11.5px] text-ads-muted hover:bg-ads-hover"
    >
      <span className="font-medium text-ads-text">{DOCK_TABS.find((t) => t.id === tab)?.label}</span>
      <span>— click to expand</span>
    </button>
  );
}
